import { createApp, watch, type App as VueApp } from 'vue';
import { createPinia } from 'pinia';
import mitt from 'mitt';
import App from '@/App.vue';
import router from '@/Router/standaloneRouter';
import {
  cssVariablesFromConfig,
  cssVariablesFromFallbacks,
  applyFaviconsFromConfig,
  applyPwaManifestFromConfig,
} from '@/Utils/App/theme';
import { createI18nInstance, loadLocale } from '@/Language/i18n';
import { bootstrapConfig } from '@/Bootstrap/config';
import { vJsRoot } from '@/Utils/Vue/jsRoot';
import { useContextStore } from '@/Stores/context';
import { useConsentStore } from '@/Stores/consent';
import { installExternalLinkGuard } from '@/Utils/Vue/externalLinkGuard';
import { initStandaloneIntegrations } from '@/Integrations/Standalone/initStandaloneIntegrations';

async function setupI18n(app: VueApp): Promise<void> {
  const context = useContextStore();
  const initialLang = context.language || 'de';
  const i18n = createI18nInstance(initialLang);

  document.documentElement.lang = initialLang;
  await loadLocale(i18n, initialLang);
  app.use(i18n);

  watch(
    () => context.language,
    async (lang) => {
      const resolvedLang = lang || 'de';
      document.documentElement.lang = resolvedLang;
      await loadLocale(i18n, resolvedLang);
    },
  );
}

function applyCssVariables(cssText: string): void {
  const configSheet = new CSSStyleSheet();
  configSheet.replaceSync(cssText);
  const sheets = [...document.adoptedStyleSheets.filter((s) => s !== configSheet), configSheet];
  document.adoptedStyleSheets = sheets;
}

async function bootstrap() {
  const initialPathParts = window.location.pathname.split('/').filter(Boolean);
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(router);
  app.directive('js-root', vJsRoot);
  app.config.globalProperties.emitter = mitt();

  await router.isReady();

  const { load } = await bootstrapConfig({
    mode: 'standalone',
    route: router.currentRoute.value,
  });

  await setupI18n(app);

  if (!load.valid) {
    const currentRoute = router.currentRoute.value;

    if (currentRoute.name !== 'NotFound') {
      await router.replace({
        name: 'NotFound',
        params: { catchAll: initialPathParts },
        query: currentRoute.query,
        hash: currentRoute.hash,
        state: {
          notFoundPayload: {
            reason: load.code,
            missing: load.missing,
          },
        },
      });
    }

    applyCssVariables(
      useContextStore().experience
        ? cssVariablesFromConfig('standalone')
        : cssVariablesFromFallbacks('standalone'),
    );
    installExternalLinkGuard(app);
    app.mount('#app');
    return;
  }

  useConsentStore().ensureExternalContentConsent();
  await initStandaloneIntegrations();
  applyCssVariables(cssVariablesFromConfig('standalone'));
  applyFaviconsFromConfig();
  applyPwaManifestFromConfig();

  installExternalLinkGuard(app);

  app.mount('#app');
}

bootstrap();
